import { Choice, Footer, Hint } from './styles';
import { useId, useState } from 'react';
import styled from 'styled-components';
import { Button, Dialog } from '@/ui';
import { Icon } from '@/icons';
import { useDiscussions } from '@/hooks/useDiscussions';
import { discussionsService, type FollowMode } from '@/core/services/discussionsService';

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 40px;
  min-height: 40px;
  flex-shrink: 0;
  padding: 6px;
  color: ${({ theme }) => theme.color.textMuted};
  background: transparent;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &[data-following='true'] {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;
const modes: [FollowMode, string, string][] = [
  ['off', 'Not following', 'Remove this from your followed discussions.'],
  ['silent', 'Follow silently', 'Keep it in Discussions you follow, without alerts.'],
  ['notify', 'Notify me', 'Group new replies into an alert using your notification settings.'],
];

export function DiscussionBell({
  postId,
  commentId = null,
}: {
  postId: string;
  commentId?: string | null;
}) {
  const { owner, follows, prefs, ready, error: loadError } = useDiscussions();
  const current =
    follows.find((f) => f.postId === postId && f.commentId === commentId)?.mode ?? 'off';
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FollowMode>('off');
  const [enable, setEnable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const name = useId();
  if (!owner) return null;
  async function save() {
    if (!owner || busy) return;
    setBusy(true);
    setError('');
    try {
      await discussionsService.follow(owner, postId, commentId, draft, enable);
      setOpen(false);
    } catch {
      setError('Could not save. Your previous choice is unchanged. Try again.');
    } finally {
      setBusy(false);
    }
  }
  const label = `${current === 'off' ? 'Follow' : 'Following'} ${commentId ? 'this comment thread' : 'this discussion'}`;
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Trigger
        type="button"
        aria-label={label}
        title={`${label}${current === 'silent' ? ' silently' : current === 'notify' ? ' with notifications' : ''}`}
        aria-haspopup="dialog"
        data-following={current !== 'off'}
        onClick={() => {
          setDraft(current);
          setEnable(false);
          setError('');
          setOpen(true);
        }}
      >
        <Icon
          name={
            current === 'notify' ? 'bell-ringing' : current === 'silent' ? 'bell-check' : 'bell'
          }
          size={19}
        />
      </Trigger>
      {open && (
        <Dialog
          open
          title={commentId ? 'Follow this comment thread' : 'Follow this discussion'}
          onClose={() => {
            if (!busy) setOpen(false);
          }}
          dismissable={!busy}
        >
          <Hint>
            {commentId
              ? 'Only replies in this comment’s branch. Other comments on the post are separate.'
              : 'All new comments and replies on this post.'}
          </Hint>
          {modes.map(([value, title, description]) => (
            <Choice key={value}>
              <input
                type="radio"
                name={name}
                checked={draft === value}
                onChange={() => setDraft(value)}
                disabled={busy}
              />
              <span>
                <strong>{title}</strong>
                <small>{description}</small>
              </span>
            </Choice>
          ))}
          {draft === 'notify' && !prefs.inApp && !prefs.push && (
            <>
              <Hint>
                Discussion alerts are currently off. This choice will wait until you enable a
                channel.
              </Hint>
              <Choice>
                <input
                  type="checkbox"
                  checked={enable}
                  onChange={(e) => setEnable(e.target.checked)}
                  disabled={busy}
                />
                <span>Also enable in-app discussion notifications</span>
              </Choice>
            </>
          )}
          {(error || loadError) && <p role="alert">{error || loadError}</p>}
          <Footer>
            <Button variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={busy || !ready || !!loadError} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Apply'}
            </Button>
          </Footer>
        </Dialog>
      )}
    </span>
  );
}
